import { useState } from 'react';
import { Button, Loader, Tooltip } from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
import type { EmailContentResponse } from 'src/meeting-note/hooks/useGenerateEmailContent';
import { useUpdateEmailContent } from 'src/meeting-note/hooks/useGenerateEmailContent';
import type { EditableEmailFields } from 'src/common/components/EmailContentModal';
import EmailContentModal from 'src/common/components/EmailContentModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type EmailDocumentButtonProps = {
  documentId: string;
  isGenerating: boolean;
  isDeleting: boolean;
  onEmailClick: (documentId: string) => Promise<EmailContentResponse | null>;
  onDelete: (emailContentId: string) => void;
  clientName?: string;
  documentLabel?: string;
};

export default function EmailDocumentButton({
  documentId,
  isGenerating,
  isDeleting,
  onEmailClick,
  onDelete,
  clientName,
  documentLabel,
}: EmailDocumentButtonProps) {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [localContent, setLocalContent] = useState<EmailContentResponse | null>(null);
  const { updateEmailContent, isSaving } = useUpdateEmailContent();

  const hasContent = !!localContent;

  const handleClick = async () => {
    if (localContent) {
      setEmailModalOpen(true);
      return;
    }

    const result = await onEmailClick(documentId);
    if (result) {
      setLocalContent(result);
      setEmailModalOpen(true);
    }
  };

  const handleDelete = () => {
    if (!localContent) return;
    onDelete(localContent.id);
    setLocalContent(null);
    setEmailModalOpen(false);
  };

  const handleSave = async (fields: EditableEmailFields) => {
    if (!localContent) return;
    try {
      await updateEmailContent({ emailContentId: localContent.id, ...fields });
      setLocalContent(prev => (prev ? { ...prev, ...fields } : prev));
    } catch (e) {
      notifyAndLogError(
        'Failed to save email draft',
        'An error occurred while saving the email draft. Please try again.',
        e,
        { emailContentId: localContent.id },
      );
    }
  };

  const getTooltipLabel = () => {
    if (hasContent) return 'View email content';
    return 'Create an email draft from this document.';
  };

  const getButtonLabel = () => {
    if (isGenerating) return 'Preparing';
    if (hasContent) return 'View draft';
    return 'Draft email';
  };

  return (
    <>
      <Tooltip label={getTooltipLabel()}>
        <Button
          variant={hasContent ? 'outline' : 'outline-black'}
          color={hasContent ? 'success' : isGenerating ? 'dimmed' : undefined}
          radius="md"
          leftSection={isGenerating ? <Loader size={16} color="dimmed" /> : <IconMail size={16} />}
          w={135}
          onClick={handleClick}
          disabled={isGenerating}
        >
          {getButtonLabel()}
        </Button>
      </Tooltip>

      <EmailContentModal
        opened={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        content={localContent}
        onDelete={handleDelete}
        onSave={handleSave}
        isDeleting={isDeleting}
        isSaving={isSaving}
        clientName={clientName}
        documentLabel={documentLabel}
      />
    </>
  );
}
